<template>
	<!-- Reservation Confirmation -->
	<section class="reservation">
		<header>
			<div class="container p-y-2">
				<div class="row">
					<div class="col-xs-12 col-lg-8 col-lg-offset-2">
						<h1 class="h2" data-testid="confirmationHeader">Your reservation is complete.</h1>
						<p class="alert alert-info m-b-0">
							<strong>Thank you!</strong>
							<span v-if="resData.email"> An email has been sent to {{ resData.email }}.</span>
							<span v-if="!resData.email"> An email has been sent to the email addresses on file.</span>
							<span v-if="!isWorkExperience"> Additional documentation may be required prior to participating in the activity.</span>
						</p>
					</div>
				</div>
			</div>
		</header>

		<div id="acAssessment"></div>

		<div class="reservation-body">
			<div class="container">

				<div class="row p-t-2">
					<div class="col-xs-12 col-lg-8 col-lg-offset-2">
						<div class="card card-reserved">

							<div class="card-section-top">
								<h2 class="card-title-wrapper">
									<span class="h5">
										{{ resData.resourceType }}
									</span>
									<span class="h3 card-title">
										{{ resData.resourceName }}
									</span>
								</h2>
								<div v-if="resData.priceString">
									<p class="card-price">
										{{ resData.priceString }}
									</p>
									<p class="small m-t-sm">{{ resData.disclaimer }}</p>
								</div>
							</div>

							<div class="card-section">
								<div class="card-section-cell">
									<p class="m-b-0">
										<TemporalDisplay :start="resData.startMoment" template="dateFull"/>
									</p>
									<p class="m-b-0">
										<TemporalDisplay :start="resData.startMoment" :end="resData.endMoment" template="time"/>
									</p>
									<p class="m-b-0">
										{{ resData.location }}
									</p>
									<p class="m-b-0" v-if="resData.instructors.length > 0">
										<strong>With:</strong>
										<span v-html="buildInstructors(resData.instructors)"></span>
									</p>
									<button class="btn btn-primary btn-sm m-t-grid" v-if="!isMobileApp" @click="addToCalendar(resData.participants[0].registrationId)">Add to Calendar</button> <!-- TODO: Does this need to loop over multiple participants? -->
								</div>
								<div class="card-section-cell">
									<h3 class="h5">
										Participant<span v-show="resData.participants.length > 1">s</span>:
									</h3>
									<ul class="list-unstyled">
										<li v-for="participant in resData.participants" :key="participant.memberId">{{ participant.firstName }}<span v-if="participant.spot">, Spot {{ participant.spot }}</span><span v-else-if="participant.waitlistSpot">, Waitlist Spot {{ participant.waitlistSpot }}</span></li>
									</ul>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="row" v-if="resData.description && resData.whatToBring">
					<div class="col-xs-12 col-lg-8 col-lg-offset-2">
						<div class="card card-reserved">
							<div class="card-section-top">
								<h3 class="h5">Appointment Details</h3>
							</div>
							<div class="card-section">
								<div class="card-section-cell card-section-cell-noline">
									<h4 class="h6">Description</h4>
									<p>{{ resData.description }}</p>
								</div>
								<div class="card-section-cell card-section-cell-noline">
									<h4 class="h6">What to Bring</h4>
									<p>{{ resData.whatToBring }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="row" v-if="showPolicies">
					<div class="col-xs-12 col-lg-8 col-lg-offset-2">
						<p class="small">
							Reservation, cancellation, and no-show policies vary by class type. <a href="https://my.lifetime.life/policy/guest-and-club-policy.app.html?show=ltGroupFitnessandLifeStudio">View policies.</a>
						</p>
					</div>
				</div>

			</div>
		</div>

		<div class="container" v-if="!isMobileApp">
			<div class="row p-t-2">
				<div class="col-xs-12 col-lg-8 col-lg-offset-2">
					<div class="btn-wrap text-xs-left">
						<a class="btn btn-primary" :href="makeAnotherReservationUrl" data-testid="makeAnotherreservationBtn" v-if="makeAnotherReservationUrl">Make Another Reservation</a>
						<a class="btn btn-primary-outline" :href="myReservationsUrl">View All Reservations</a>
					</div>
				</div>
			</div>
		</div>

		<AddToCalendar ref="addToCalendarComponent" />

	</section>
</template>

<script>
import { defineComponent } from 'vue';
import _ from 'lodash';

import { Helpers } from '@/common/helpers';
import { FrameworkService } from '@/services/framework.service';
import TemporalDisplay from '@/components/schreg-common/TemporalDisplay.vue';
import AddToCalendar from '@/components/schreg-common/AddToCalendar.vue';


export default defineComponent({
	name: 'ConfirmationSub',
	components: {
		TemporalDisplay,
		AddToCalendar
	},
	props: {
		// Reservation data
		resData: {
			default: () => {},
			type: Object
		},
		showPolicies: {
			type: Boolean,
			default: false
		},
		regTypeName: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			isWorkExperience: false,
			isMobileApp: false,
			makeAnotherReservationUrl: '',
			myReservationsUrl: '',
			isCourt: false
		};
	},
	mounted() {
		this.isWorkExperience = FrameworkService.isWorkExperience();
		this.isMobileApp = Helpers.isMobileApp();
		this.makeAnotherReservationUrl = this.resData.newReservationUrl;
		this.myReservationsUrl = FrameworkService.globalUrl('myReservations');

		// Trigger event for PT assessment (questionnaire)
		$(document).trigger(
			'ac-assessment',
			_.map(this.resData.participants, (participant) => {
				return { memberId: participant.memberId, identifier: this.resData.mmsProductId };
			})
		);

		window.location.hash = 'registrationConfirmation'; // For analytics
	},
	methods: {
		buildInstructors(instructors) {
			return Helpers.buildInstructors(instructors);
		},
		addToCalendar(registrationId) {
			this.$refs.addToCalendarComponent.open(
				this.resData.eventId, // eventId
				registrationId, // registrationId
				false, // isSpaReservation
				null // reservationDate (needed only for Spa reservations)
			);
		}
	}
});
</script>
